import { createBrowserRouter, Navigate } from "react-router-dom";
import App from "../App";
import ProtectedApp from "../ProtectedApp";
import { ErrorPage } from "../common-ui/ErrorPage";
import { Users } from "../features/Users/Users";
import { UserInvitations } from "../features/UserInvitations/UserInvitations";
import { UserInvitation } from "../features/UserInvitations/UserInvitation";
import { NewUserInvitation } from "../features/UserInvitations/NewUserInvitation";
import { RetrieveUsername } from "../features/login/RetrieveUsername";
import { UserInstructions } from "../features/UserInvitations/UserInstructions";
import { Register } from "../features/login/Register";
import { UserInvitationsBulkUpload } from "../features/UserInvitations/UserInvitationsBulkUpload";
import { UserInvitationsBulkInvite } from "../features/UserInvitations/UserInvitationsBulkInvite";
import { Programs } from "../features/Programs/Programs";
import { NewProgram } from "../features/Programs/NewProgram";
import { Program } from "../features/Programs/Program";
import { EditProgram } from "../features/Programs/EditProgram";
import { Networks } from "../features/Networks/Networks";
import { NewNetwork } from "../features/Networks/NewNetwork";
import { Network } from "../features/Networks/Network";
import { EditNetwork } from "../features/Networks/EditNetwork";
import { Institutions } from "../features/Institutions/Institutions";
import { Institution } from "../features/Institutions/Institution";
import { NewInstitution } from "../features/Institutions/NewInstitution";
import { EditInstitution } from "../features/Institutions/EditInstitution";
import { AssessmentDefinitions } from "../features/Assessments/AssessmentDefinitions";
import { EditInstrument } from "../features/Instruments/EditInstrument";
import { Layout } from "../features/nav/Layout";
import { RootRedirect } from "../common-ui/RootRedirect";
import { PendingAssessments } from "../features/Assessments/PendingAssessments";
import { CreateAssessmentEvent } from "../features/Assessments/CreateAssessmentEvent/CreateAssessmentEvent";
import { CompleteAssessmentEvent } from "../features/AssessmentEvents/CompleteAssessmentEvent/CompleteAssessmentEvent";
import { AssessmentDefinition } from "../features/Assessments/AssessmentDefinition";
import { ProtectedRoute } from "../features/nav/ProtectedRoute";
import { Role } from "../features/Auth/roles";
import { canUseCoaching, canUseRotations } from "../features/Auth/adminAccess";
import { hasActiveTraineeOrAttendingMembership } from "./utils";
import { Redirect } from "../features/Redirection/Redirect";
import { Logout } from "../features/login/Logout";
import { AssessmentRoles } from "../features/Assessments/CreateAssessmentEvent/AssessmentRoles";
import { CompleteRegistration } from "../features/UserInvitations/CompleteRegistration";
import { AssessmentHistoryDebug } from "../features/Debug/AssessmentHistoryDebug";
import { QueryExplorer } from "../features/Debug/QueryExplorer";
import { ProgramAssessmentsPage } from "../features/ProgramAssessments/ProgramAssessmentsPage";
import { ModuleBuilderPage } from "../features/ModuleBuilder/ModuleBuilderPage";
import { Coaching } from "../features/T13Admin/Coaching";
import { Rotations } from "../features/T13Admin/Rotations";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <ProtectedApp />,
        children: [
          {
            path: "/",
            element: <Layout />,
            children: [
              {
                path: "/",
                element: <RootRedirect />
              },
              // User-Level Routes (Trainees, Attendings)
              {
                element: (
                  <ProtectedRoute
                    requirePredicate={({ currentUser }) =>
                      hasActiveTraineeOrAttendingMembership(currentUser)
                    }
                  />
                ),
                children: [
                  {
                    path: "assessments/pending",
                    element: <PendingAssessments />
                  },
                  {
                    path: "assessments/create",
                    element: <AssessmentRoles />
                  },
                  {
                    path: "assessments/complete/:assessmentEventId",
                    element: <CompleteAssessmentEvent />
                  }
                ]
              },
              // ProgramAdmin-Level Routes
              {
                element: (
                  <ProtectedRoute
                    requiredRoles={[Role.ProgramAdmin, Role.SystemAdmin]}
                  />
                ),
                children: [
                  {
                    path: "users",
                    element: <Users />
                  },
                  {
                    path: "invitations",
                    element: <UserInvitations />
                  },
                  {
                    path: "invitations/:id",
                    element: <UserInvitation />
                  },
                  {
                    path: "invitations/new",
                    element: <NewUserInvitation />
                  },
                  {
                    path: "invitations/bulkupload",
                    element: <UserInvitationsBulkUpload />
                  },
                  {
                    path: "invitations/bulkinvite",
                    element: <UserInvitationsBulkInvite />
                  },
                  {
                    path: "program-assessments",
                    element: <ProgramAssessmentsPage />
                  },
                  {
                    path: "reports/program-assessments",
                    element: <Navigate to="/program-assessments" replace />
                  },
                  // SE-1765 T13: Coaching is ABP-only and for System /
                  // Program Admins. Same predicates as the tab in Layout,
                  // so the two never disagree on who sees the page.
                  {
                    element: (
                      <ProtectedRoute
                        requirePredicate={({
                          networkFeatures,
                          adminAuthority
                        }) =>
                          networkFeatures.coaching &&
                          canUseCoaching(adminAuthority)
                        }
                      />
                    ),
                    children: [
                      {
                        path: "coaching",
                        element: <Coaching />
                      }
                    ]
                  }
                ]
              },
              // SE-1765 T13: Rotations is ABP-only and open to System /
              // Program Admins and Rotation Directors (who get a limited,
              // own-rotations-only view). Outside the admin section so a
              // director who isn't an admin can reach it.
              {
                element: (
                  <ProtectedRoute
                    requirePredicate={({ networkFeatures, adminAuthority }) =>
                      networkFeatures.rotations &&
                      canUseRotations(adminAuthority)
                    }
                  />
                ),
                children: [
                  {
                    path: "rotations",
                    element: <Rotations />
                  }
                ]
              },
              // SysAdmin-Level Routes
              {
                element: (
                  <ProtectedRoute requiredRoles={[Role.SystemAdmin]} />
                ),
                children: [
                  {
                    path: "assessmentdefinitions",
                    element: <AssessmentDefinitions />
                  },
                  {
                    path: "programs",
                    element: <Programs />
                  },
                  {
                    path: "programs/new",
                    element: <NewProgram />
                  },
                  {
                    path: "programs/:id",
                    element: <Program />
                  },
                  {
                    path: "programs/:id/edit",
                    element: <EditProgram />
                  },
                  {
                    path: "networks",
                    element: <Networks />
                  },
                  {
                    path: "networks/new",
                    element: <NewNetwork />
                  },
                  {
                    path: "networks/:id",
                    element: <Network />
                  },
                  {
                    path: "networks/:id/edit",
                    element: <EditNetwork />
                  },
                  {
                    path: "institutions",
                    element: <Institutions />
                  },
                  {
                    path: "institutions/:id",
                    element: <Institution />
                  },
                  {
                    path: "institutions/new",
                    element: <NewInstitution />
                  },
                  {
                    path: "institutions/:id/edit",
                    element: <EditInstitution />
                  },
                  {
                    path: "assessmentdefinitions",
                    element: <AssessmentDefinitions />
                  },
                  {
                    path: "assessmentdefinitions/:id",
                    element: <AssessmentDefinition />
                  },
                  {
                    path: "instruments/:id/edit",
                    element: <EditInstrument />
                  },
                  {
                    path: "debug/assessment-history",
                    element: <AssessmentHistoryDebug />
                  },
                  {
                    path: "debug/queries",
                    element: <QueryExplorer />
                  },
                  {
                    path: "module-builder",
                    element: <ModuleBuilderPage />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: "/logout",
        element: <Logout />
      },
      {
        path: "/retrieveusername",
        element: <RetrieveUsername />
      },
      {
        path: "/v9Jo",
        element: <Redirect />
      },
      {
        path: "/confirmaccountcreation/:id",
        element: <CompleteRegistration />
      },
      {
        path: "/userinstructions",
        element: <UserInstructions />
      },
      {
        path: "/register",
        element: <Register />
      },
      {
        path: "/complete-registration",
        element: <CompleteRegistration />
      }
    ]
  }
]);
